// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
/**
 * Cognito OAuth types and token exchange service.
 *
 * Implements the authorization code grant flow against the Cognito
 * Managed Login hosted UI. Tokens are kept in memory only — never
 * persisted to localStorage or sessionStorage.
 */

export interface CognitoOAuthConfig {
  /** Cognito Managed Login base URL */
  loginUrl: string;
  /** Cognito logout endpoint */
  logoutUrl: string;
  /** User Pool Client ID */
  clientId: string;
  /** CloudFront URL (callback) */
  redirectUri: string;
  /** Cognito /oauth2/token endpoint */
  tokenEndpoint: string;
}

export interface AuthState {
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  /** Unix timestamp (ms) for access token expiry */
  expiresAt: number | null;
}

export interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * Generate a cryptographically random state string for OAuth CSRF protection.
 */
function generateOAuthState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
}

// ── PKCE (RFC 7636) ──────────────────────────────────────────────────

/**
 * Generate a random PKCE code_verifier (43-128 characters, URL-safe).
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  // Base64url encode without padding
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Compute the S256 code_challenge from a code_verifier.
 */
async function computeCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  // Base64url encode without padding
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Build the Cognito Managed Login `/authorize` redirect URL.
 * Generates and stores:
 * - A random `state` parameter in sessionStorage for CSRF protection
 * - A PKCE `code_verifier` in sessionStorage for code exchange protection
 * Includes `code_challenge` (S256) in the authorize URL.
 */
export async function buildLoginRedirectUrl(config: CognitoOAuthConfig): Promise<string> {
  const state = generateOAuthState();
  sessionStorage.setItem("oauth_state", state);

  const codeVerifier = generateCodeVerifier();
  sessionStorage.setItem("pkce_code_verifier", codeVerifier);
  const codeChallenge = await computeCodeChallenge(codeVerifier);

  return (
    `${config.loginUrl}/authorize` +
    `?client_id=${encodeURIComponent(config.clientId)}` +
    `&redirect_uri=${encodeURIComponent(config.redirectUri)}` +
    `&response_type=code` +
    `&scope=openid+email+profile` +
    `&state=${encodeURIComponent(state)}` +
    `&code_challenge=${encodeURIComponent(codeChallenge)}` +
    `&code_challenge_method=S256`
  );
}

/**
 * Validate the OAuth state parameter from the callback against the stored value.
 * Returns true if valid, false if missing or mismatched.
 * Consumes the stored state (single-use).
 */
export function validateOAuthState(returnedState: string | null): boolean {
  const storedState = sessionStorage.getItem("oauth_state");
  sessionStorage.removeItem("oauth_state");

  if (!storedState || !returnedState) {
    return false;
  }
  return storedState === returnedState;
}

/**
 * Exchange an authorization code for tokens via the Cognito token endpoint.
 * Includes the PKCE code_verifier from sessionStorage.
 */
export async function exchangeCodeForTokens(
  code: string,
  config: CognitoOAuthConfig,
): Promise<AuthState> {
  // Retrieve and consume the PKCE code_verifier
  const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
  sessionStorage.removeItem("pkce_code_verifier");

  const params: Record<string, string> = {
    grant_type: "authorization_code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    code,
  };

  if (codeVerifier) {
    params.code_verifier = codeVerifier;
  }

  const body = new URLSearchParams(params);

  const response = await fetch(config.tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status}`);
  }

  const data: TokenResponse = await response.json();

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };
}

/**
 * Refresh an access token using a refresh token.
 */
export async function refreshAccessToken(
  refreshToken: string,
  config: CognitoOAuthConfig,
): Promise<AuthState> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: config.clientId,
    refresh_token: refreshToken,
  });

  const response = await fetch(config.tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error(`Token refresh failed: ${response.status}`);
  }

  const data: TokenResponse = await response.json();

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token ?? refreshToken,
    expiresAt: Date.now() + data.expires_in * 1000,
  };
}
